import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useStore } from 'react-redux';
import { useIsomorphicLayoutEffect, useRouter, useServices } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { cloneDeep } from 'core/common/utils/cloneDeep';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { Flow } from 'core/flow/entities';
import { FunnelConfig } from 'core/funnel/entities';
import { getConditionAttributes } from 'core/funnel/store/selectors';
import i18n from 'core/i18n';
import { Languages } from 'core/localization/entities';
import { useAnalyticsIds } from 'core/scripts/hooks';
import { useUserLocation } from 'core/user/hooks';
import { useFlow } from '../hooks';

type FunnelContextData = {
  flow: Flow;
  analyticsIds: {
    facebook: Array<string>;
    tikTok: Array<string>;
    snapchat: Array<string>;
  };
} & Omit<FunnelConfig, 'flows' | 'analytics'>;

export const FunnelContext = createContext<FunnelContextData>(undefined!);

type FunnelProviderProps = {
  children: ReactNode;
  resources: FunnelConfig;
};

export const FunnelProvider = ({ children, resources }: FunnelProviderProps) => {
  const { locale = 'en' } = useRouter();

  const featureFlags = useFeatureFlags();

  const { analyticsService, errorMonitoringService, funnelSettingsService } = useServices();
  const { fetchUserLocation } = useUserLocation();
  const { updateFlow } = useFlow();

  const store = useStore<RootInitialState>();

  const { flows, analytics, ...quizWithoutFlows } = resources;

  const activeFlow: Flow = flows[0];

  const funnelConditions = getConditionAttributes(store.getState());
  const funnelName = activeFlow.attributes.overriddenFunnelName || resources.name;

  const analyticsPixelIds = useAnalyticsIds({
    funnelAnalytics: analytics,
    funnelConditions,
  });

  useIsomorphicLayoutEffect(() => {
    fetchUserLocation();
    funnelSettingsService.setProjectId(resources.project.id);
    funnelSettingsService.setFunnelId(resources.id);
    funnelSettingsService.setFunnelName(funnelName);
    funnelSettingsService.setFunnelLocale(locale);
    funnelSettingsService.setFlowId(activeFlow.id);
    funnelSettingsService.setFlowName(activeFlow.name);

    errorMonitoringService.addCustomTags({
      funnel_name: funnelName,
      flow_name: activeFlow.name,
      funnel_locale: locale,
    });

    analyticsService.funnelOpened({
      funnel: funnelName,
      flow: activeFlow.name,
      language: activeFlow.language,
    });
  }, [
    errorMonitoringService,
    funnelSettingsService,
    funnelName,
    locale,
    activeFlow,
    resources.project.id,
    resources.id,
  ]);

  useEffect(() => {
    if (!activeFlow) {
      i18n.changeLanguage(Languages.EN);

      return;
    }

    i18n.changeLanguage(activeFlow.language);

    updateFlow(activeFlow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow]);

  if (!featureFlags.getAttributes().funnel) {
    featureFlags.setAttributes({ funnel: resources.name });
  }

  if (activeFlow && !featureFlags.getAttributes().flowName) {
    featureFlags.setAttributes({ flowName: activeFlow.name });
  }

  if (activeFlow && activeFlow.translations) {
    i18n.addResourceBundle(
      activeFlow.language,
      'translation',
      // in cause when activeFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(activeFlow.translations),
      true,
      true,
    );
  }

  return (
    <FunnelContext.Provider
      value={{
        ...quizWithoutFlows,
        flow: activeFlow,
        analyticsIds: {
          facebook: analyticsPixelIds.facebook.combined,
          tikTok: analyticsPixelIds.tikTok.combined,
          snapchat: analyticsPixelIds.snapchat.combined,
        },
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};

export const useFunnelData = () => {
  const context = useContext(FunnelContext);

  if (!context) {
    throw new Error('useFunnelData must be used within a FunnelProvider');
  }

  return context;
};
